import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import {
    AzureMLExecutePipelineActivity,
    MachineLearningExecutionType,
    MachineLearningPipelineEndpointInputFormat,
    MachineLearningPipelineType
} from 'app/model/pipeline/azure-ml-execute-pipeline.activity';
import { linkedServiceReferenceWithoutParameterizationRemainer } from 'app/model/adapters/reference-remainer';
import { AzureMLServiceLinkedService } from 'app/model/linkedservice/azure-ml-service.linkedservice';
import {
    ActivityConnectionContext,
    IAzureMlExecutePipelineActivityContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { LinkedService, ModelConstants } from 'shell-models-lib';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ModelService } from 'app/model/services/model.service';
import { IFrameService } from 'app/services/iframe.service';
import { getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureMLExecutePipelineActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        userProperties: '*',
        description: '*',
        dependsOn: '*',
        policy: '*',
        typeProperties: {
            mlPipelineId: '*',
            mlPipelineEndpointId: '*',
            version: '*',
            dataPathAssignments: '*',
            experimentName: '*',
            mlPipelineParameters: '*',
            mlParentRunId: '*',
            continueOnStepFailure: '*',
            mlBatchEndpointName: '*',
            mlBatchDeploymentName: '*',
            mlExecutionType: '*',
            jobSettings: '*',
            jobInputs: '*',
            jobOutputs: '*'
        }
    }
})
export class AzureMLExecutePipelineActivityAdapter extends ActivityAsyncAdapter<
    AzureMLExecutePipelineActivity,
    Model.AzureMLExecutePipelineActivity,
    IAzureMlExecutePipelineActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(AzureMLExecutePipelineActivity);
    }

    public override async toFabricCopilotContext(
        activity: AzureMLExecutePipelineActivity
    ): Promise<IAzureMlExecutePipelineActivityContext> {
        if (activity) {
            const activityContext: IAzureMlExecutePipelineActivityContext = await super.toFabricCopilotContext(
                activity
            );
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    LinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
                activityContext.EndpointType =
                    activity.mlExecutionType === MachineLearningExecutionType.ML_PIPELINE
                        ? 'PipelineV1'
                        : 'BatchEndpoint';
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IAzureMlExecutePipelineActivityContext,
        model?: AzureMLExecutePipelineActivity
    ): Promise<AzureMLExecutePipelineActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.linkedService = (
                    await findLinkedServiceFromContext<AzureMLServiceLinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = this.context.asyncAdapterFor<
                        AzureMLServiceLinkedService,
                        Model.LinkedServiceResource,
                        ActivityConnectionContext
                    >(Metamodel.modelByName(context.Connection.Type));
                    await lsAdapter?.fromFabricCopilotContext(context.Connection, model.linkedService);
                    model.mlExecutionType =
                        context.EndpointType === 'PipelineV1'
                            ? MachineLearningExecutionType.ML_PIPELINE
                            : MachineLearningExecutionType.ML_BATCH_DEPLOYMENT;
                }
            }
        }
        return model;
    }

    protected async _toResource(
        activity: AzureMLExecutePipelineActivity
    ): Promise<Model.AzureMLExecutePipelineActivity> {
        const modelAzureMLExecutePipelineActivity: Model.AzureMLExecutePipelineActivity =
            super._getCommonActivityProperties(
                activity,
                Metamodel.modelFor(AzureMLExecutePipelineActivity).name
            ) as Model.AzureMLExecutePipelineActivity;
        const azureMLExecutePipelineActivity: AzureMLExecutePipelineActivity =
            activity as AzureMLExecutePipelineActivity;

        if (activity.mlExecutionType === MachineLearningExecutionType.ML_PIPELINE) {
            modelAzureMLExecutePipelineActivity.typeProperties = {
                experimentName: ValueAdapter.toResource(activity, AzureMLExecutePipelineActivity.EXPERIMENT_NAME),
                mlPipelineParameters: toParameterObject(activity.mlPipelineParameters),
                mlParentRunId: ValueAdapter.toResource(activity, AzureMLExecutePipelineActivity.ML_PARENT_RUN_ID),
                mlExecutionType: ValueAdapter.toResource(activity, AzureMLExecutePipelineActivity.ML_EXECUTION_TYPE),
                continueOnStepFailure: activity.continueOnStepFailure
            };
            if (activity.pipelineFormat === MachineLearningPipelineType.ML_PIPELINE_ID) {
                modelAzureMLExecutePipelineActivity.typeProperties.mlPipelineId = ValueAdapter.toResource(
                    activity,
                    AzureMLExecutePipelineActivity.ML_PIPELINE_ID
                );
            }

            if (activity.pipelineFormat === MachineLearningPipelineType.ML_PIPELINE_ENDPOINT_ID) {
                modelAzureMLExecutePipelineActivity.typeProperties.mlPipelineEndpointId = ValueAdapter.toResource(
                    activity,
                    AzureMLExecutePipelineActivity.ML_PIPELINE_ENDPOINT_ID
                );
                if (ValueAdapter.toResource(activity, AzureMLExecutePipelineActivity.VERSION)) {
                    modelAzureMLExecutePipelineActivity.typeProperties.version = ValueAdapter.toResource(
                        activity,
                        AzureMLExecutePipelineActivity.VERSION
                    );
                }
            }

            if (activity.dataPathAssignments && activity.dataPathAssignments.length) {
                modelAzureMLExecutePipelineActivity.typeProperties.dataPathAssignments = {};
                activity.dataPathAssignments.forEach((param) => {
                    modelAzureMLExecutePipelineActivity.typeProperties.dataPathAssignments[param.name] =
                        toParameterObject(param.values);
                });
            }
        } else if (activity.mlExecutionType === MachineLearningExecutionType.ML_BATCH_DEPLOYMENT) {
            modelAzureMLExecutePipelineActivity.typeProperties = {
                mlExecutionType: ValueAdapter.toResource(activity, AzureMLExecutePipelineActivity.ML_EXECUTION_TYPE),
                jobSettings: toParameterObject(activity.jobSettings),
                mlBatchEndpointName: ValueAdapter.toResource(
                    activity,
                    AzureMLExecutePipelineActivity.ML_BATCH_ENDPOINT_NAME
                ),
                mlBatchDeploymentName: ValueAdapter.toResource(
                    activity,
                    AzureMLExecutePipelineActivity.ML_BATCH_DEPLOYMENT_NAME
                )
            };

            if (activity.jobInputs && activity.jobInputs.length) {
                modelAzureMLExecutePipelineActivity.typeProperties.jobInputs = {};
                activity.jobInputs.forEach((param) => {
                    modelAzureMLExecutePipelineActivity.typeProperties.jobInputs[param.name] = toParameterObject(
                        param.values
                    );
                });
            }

            if (activity.jobOutputs && activity.jobOutputs.length) {
                modelAzureMLExecutePipelineActivity.typeProperties.jobOutputs = {};
                activity.jobOutputs.forEach((param) => {
                    modelAzureMLExecutePipelineActivity.typeProperties.jobOutputs[param.name] = toParameterObject(
                        param.values
                    );
                });
            }
        }

        if (azureMLExecutePipelineActivity.linkedService) {
            modelAzureMLExecutePipelineActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                azureMLExecutePipelineActivity.linkedService
            );
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelAzureMLExecutePipelineActivity, parsed);
        }

        return modelAzureMLExecutePipelineActivity;
    }

    protected async _toModel(
        modelActivity: Model.AzureMLExecutePipelineActivity
    ): Promise<AzureMLExecutePipelineActivity> {
        const activity: AzureMLExecutePipelineActivity =
            AzureMLExecutePipelineActivity.MODEL.create<AzureMLExecutePipelineActivity>(
                modelActivity.name,
                modelActivity.description
            );
        const modelAzureMLExecutePipelineActivity = modelActivity as Model.AzureMLExecutePipelineActivity;

        this._updatedCommonProperties(modelActivity, activity);

        if (modelAzureMLExecutePipelineActivity.typeProperties) {
            ValueAdapter.toModel(
                activity,
                AzureMLExecutePipelineActivity.EXPERIMENT_NAME,
                modelAzureMLExecutePipelineActivity.typeProperties.experimentName
            );
            activity.mlPipelineParameters = toParameterClass(
                modelActivity.typeProperties.mlPipelineParameters,
                activity
            );
            ValueAdapter.toModel(
                activity,
                AzureMLExecutePipelineActivity.ML_PARENT_RUN_ID,
                modelAzureMLExecutePipelineActivity.typeProperties.mlParentRunId
            );
            activity.continueOnStepFailure = modelAzureMLExecutePipelineActivity.typeProperties.continueOnStepFailure;
            ValueAdapter.toModel(
                activity,
                AzureMLExecutePipelineActivity.ML_EXECUTION_TYPE,
                modelAzureMLExecutePipelineActivity.typeProperties.mlExecutionType
            );
            activity.jobSettings = toParameterClass(modelActivity.typeProperties.jobSettings, activity);
        }

        if (modelAzureMLExecutePipelineActivity.typeProperties.mlPipelineId) {
            activity.pipelineFormat = MachineLearningPipelineType.ML_PIPELINE_ID;
            ValueAdapter.toModel(
                activity,
                AzureMLExecutePipelineActivity.ML_PIPELINE_ID,
                modelAzureMLExecutePipelineActivity.typeProperties.mlPipelineId
            );
        }

        if (modelAzureMLExecutePipelineActivity.typeProperties.mlPipelineEndpointId) {
            activity.pipelineFormat = MachineLearningPipelineType.ML_PIPELINE_ENDPOINT_ID;
            ValueAdapter.toModel(
                activity,
                AzureMLExecutePipelineActivity.ML_PIPELINE_ENDPOINT_ID,
                modelAzureMLExecutePipelineActivity.typeProperties.mlPipelineEndpointId
            );

            if (ValueAdapter.isTemplated(activity, AzureMLExecutePipelineActivity.ML_PIPELINE_ENDPOINT_ID)) {
                activity.pipelineEndpointInputFormat =
                    MachineLearningPipelineEndpointInputFormat.ML_PIPELINE_ENDPOINT_ID;
            }
            if (modelAzureMLExecutePipelineActivity.typeProperties.version) {
                ValueAdapter.toModel(
                    activity,
                    AzureMLExecutePipelineActivity.VERSION,
                    modelAzureMLExecutePipelineActivity.typeProperties.version
                );
            }
        }

        if (modelAzureMLExecutePipelineActivity.typeProperties.dataPathAssignments) {
            for (const item in modelAzureMLExecutePipelineActivity.typeProperties.dataPathAssignments) {
                activity.dataPathAssignments.push({
                    name: item,
                    values: toParameterClass(
                        modelAzureMLExecutePipelineActivity.typeProperties.dataPathAssignments[item],
                        activity
                    )
                });
            }
        }

        if (modelAzureMLExecutePipelineActivity.typeProperties.jobInputs) {
            for (const item in modelAzureMLExecutePipelineActivity.typeProperties.jobInputs) {
                activity.jobInputs.push({
                    name: item,
                    values: toParameterClass(
                        modelAzureMLExecutePipelineActivity.typeProperties.jobInputs[item],
                        activity
                    )
                });
            }
        }

        if (modelAzureMLExecutePipelineActivity.typeProperties.jobOutputs) {
            for (const item in modelAzureMLExecutePipelineActivity.typeProperties.jobOutputs) {
                activity.jobOutputs.push({
                    name: item,
                    values: toParameterClass(
                        modelAzureMLExecutePipelineActivity.typeProperties.jobOutputs[item],
                        activity
                    )
                });
            }
        }

        if (modelAzureMLExecutePipelineActivity.typeProperties.mlBatchEndpointName) {
            ValueAdapter.toModel(
                activity,
                AzureMLExecutePipelineActivity.ML_BATCH_ENDPOINT_NAME,
                modelAzureMLExecutePipelineActivity.typeProperties.mlBatchEndpointName
            );
        }

        if (modelAzureMLExecutePipelineActivity.typeProperties.mlBatchDeploymentName) {
            ValueAdapter.toModel(
                activity,
                AzureMLExecutePipelineActivity.ML_BATCH_DEPLOYMENT_NAME,
                modelAzureMLExecutePipelineActivity.typeProperties.mlBatchDeploymentName
            );
        }

        if (modelAzureMLExecutePipelineActivity.linkedServiceName !== undefined) {
            const ls =  getFabricLsForParameterizedConnections(
                        modelAzureMLExecutePipelineActivity.linkedServiceName,
                        ModelConstants.azureMlLinkedService, 
                        activity);
            activity.linkedService = ls ? (ls as AzureMLServiceLinkedService) :
                activity.linkedService = ReferenceAdapter.toProxyModel<AzureMLServiceLinkedService>(
                    Metamodel.modelFor(AzureMLServiceLinkedService),
                    modelAzureMLExecutePipelineActivity.linkedServiceName,
                    this.context
                );
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(
                this.context,
                modelAzureMLExecutePipelineActivity,
                AzureMLExecutePipelineActivity.MODEL
            )
        );

        return activity;
    }
}
